import Block from 'quill/blots/block';
import type { BlockEmbed } from 'quill/blots/block';
import Quill, { Delta } from 'quill/core';
import type { Range } from 'quill/core';
import BaseKeyboard from '../BaseKeyboard';
import type QuillEditor from '../QuillEditor';
import type { Blot } from '../types';
import { getSiblingLine, isRangeACaret } from '../utils/keyboardUtils';
import moveCaretIntoLineWithKeyboard from '../utils/moveCaretIntoLineWithKeyboard';
import applyBlockFormat from './applyBlockFormat';

const INDENTATION = '  ';

const endsWithNewlineChar = (delta: Delta) => {
  const lastOp = delta.ops[delta.ops.length - 1];
  return typeof lastOp?.insert === 'string' && lastOp.insert.endsWith('\n');
};

class CodeEmbedEditorKeyboard extends BaseKeyboard {
  blot: BlockEmbed;
  parentQuill: Quill;

  constructor(
    quill: Quill,
    options: { blot: BlockEmbed; parentQuill: QuillEditor },
  ) {
    const { blot, parentQuill, ...otherOptions } = options;
    super(quill, otherOptions);

    this.blot = blot;
    this.parentQuill = parentQuill;

    // Shift + Enter to exit code embed
    this.prependBinding({
      key: 'Enter',
      collapsed: true,
      shiftKey: true,
      handler: (range: Range, context: { offset: number; line: Blot }) => {
        this.exitCodeBlock(range, context);
      },
    });

    this.prependBinding({
      key: 'a',
      shortKey: true,
      handler() {
        quill.setSelection(0, quill.getLength() - 1, Quill.sources.USER);
      },
    });

    this.prependBinding({
      key: 'Backspace',
      collapsed: true,
      offset: 0,
      handler(range: Range) {
        if (range.index > 0) return true;

        const prev = getSiblingLine(blot, 'previous');

        if (
          prev !== parentQuill.titleLine &&
          prev?.statics.blotName === 'block' &&
          prev.length() <= 1
        ) {
          const blotOffset = blot.offset(parentQuill.scroll);

          // Delete the previous line if it is empty
          parentQuill.updateContents(
            new Delta().retain(blotOffset - 1).delete(1),
            Quill.sources.USER,
          );
          parentQuill.setSelection(blotOffset - 1, 0, Quill.sources.SILENT);
          return false;
        }

        // Merge with the adjacent code embed
        const isPrevLineCodeEmbed = prev?.statics.blotName === 'code-embed';
        if (isPrevLineCodeEmbed) {
          // @ts-expect-error
          const code = prev.code();
          parentQuill.updateContents(
            new Delta()
              .retain(blot.offset(parentQuill.scroll) - 1)
              .delete(1)
              .retain({ 'code-embed': new Delta().insert(code).ops }),
            Quill.sources.USER,
          );
          quill.setSelection(code.length, 0, Quill.sources.SILENT);
          return false;
        }

        const [line] = quill.getLine(0);
        if (!line) return false;
        const lineLength = line.length();
        const lengthToDeleteFromPrevBlock = prev instanceof Block ? 1 : 0;
        const blotOffset = blot.offset(parentQuill.scroll);
        let delta = new Delta()
          .retain(blotOffset - lengthToDeleteFromPrevBlock)
          .delete(lengthToDeleteFromPrevBlock)
          .concat(line.delta());

        if (prev instanceof Block) {
          const prevFormats = prev.formats();
          if (Object.keys(prevFormats).length > 0) {
            const formatDelta = new Delta()
              .retain(blotOffset - 1 + lineLength - 1)
              .retain(1, prevFormats);
            delta = delta.compose(formatDelta);
          }
        }

        if (quill.getLength() === lineLength) {
          delta.delete(1);
        } else {
          delta.retain({ 'code-embed': new Delta().delete(lineLength).ops });
        }
        parentQuill.updateContents(delta, Quill.sources.USER);
        parentQuill.setSelection(
          blotOffset - lengthToDeleteFromPrevBlock,
          0,
          Quill.sources.SILENT,
        );
        return false;
      },
    });

    this.prependBinding({
      key: 'Tab',
      shiftKey: null,
      handler(range: Range, { event }: { event: { shiftKey: boolean } }) {
        if (range.length === 0 && !event.shiftKey) {
          quill.insertText(range.index, INDENTATION, Quill.sources.USER);
          quill.setSelection(
            range.index + INDENTATION.length,
            0,
            Quill.sources.SILENT,
          );
          return;
        }

        const lines =
          range.length === 0
            ? quill.getLines(range.index, 1)
            : quill.getLines(range);
        let { index, length } = range;
        lines.forEach((line, i) => {
          if (!event.shiftKey) {
            line.insertAt(0, INDENTATION);
            if (i === 0) {
              index += INDENTATION.length;
            } else {
              length += INDENTATION.length;
            }
          } else if (line.domNode.textContent?.startsWith(INDENTATION)) {
            line.deleteAt(0, INDENTATION.length);
            if (i === 0) {
              index -= INDENTATION.length;
            } else {
              length -= INDENTATION.length;
            }
          }
        });
        quill.update(Quill.sources.USER);
        quill.setSelection(index, length, Quill.sources.SILENT);
      },
    });

    // CMD + OPTION + 0 to remove formats
    this.prependBinding({
      key: 48,
      shortKey: true,
      altKey: true,
      handler(range: Range) {
        applyBlockFormat(quill, parentQuill, blot, null, range);
      },
    });

    this.prependBinding({
      key: ['ArrowDown', 'ArrowUp'],
      handler(range: Range, { event, line }) {
        if (
          (event.key === 'ArrowUp' && line.prev) ||
          (event.key === 'ArrowDown' &&
            this.quill.getLine(range.index + range.length)[0]?.next)
        ) {
          return true;
        }
        const sibling = getSiblingLine(
          blot,
          event.key === 'ArrowUp' ? 'previous' : 'next',
        );
        if (!sibling) return true;
        const xOffset = this.quill.selection.getBounds(
          event.key === 'ArrowUp' ? range.index : range.index + range.length,
        )?.left;
        if (typeof xOffset !== 'number') return true;

        moveCaretIntoLineWithKeyboard(sibling, {
          direction: event.key === 'ArrowUp' ? 'up' : 'down',
          xOffset,
        });
        return false;
      },
    });

    this.prependBinding({
      key: ['ArrowLeft', 'ArrowRight'],
      handler(range: Range, { event, line, offset }) {
        if (!isRangeACaret(range, line)) return true;

        if (
          (event.key === 'ArrowLeft' && (line.prev || offset !== 0)) ||
          (event.key === 'ArrowRight' &&
            (line.next || offset !== line.length() - 1))
        ) {
          return true;
        }

        const sibling = getSiblingLine(
          blot,
          event.key === 'ArrowLeft' ? 'previous' : 'next',
        );
        if (!sibling) return true;

        moveCaretIntoLineWithKeyboard(sibling, {
          direction: event.key === 'ArrowLeft' ? 'left' : 'right',
        });
        return false;
      },
    });
  }

  private exitCodeBlock(
    range: Range,
    context: { offset: number; line: Blot },
    prefixLengthToDelete = 0,
  ) {
    const { blot, parentQuill: quill } = this;
    const blotOffset = blot.offset(quill.scroll);
    const value = new Delta(blot.value()['code-embed']);

    const offsetIndex = range.index - prefixLengthToDelete;
    const index =
      context.offset === context.line.length() - 1
        ? offsetIndex + 1
        : offsetIndex;

    const delta = new Delta().retain(blotOffset);
    let advancedIndex = 1;
    if (index <= 1) {
      delta.delete(1);
      advancedIndex -= 1;
    } else {
      delta.retain({
        'code-embed': new Delta()
          .retain(index)
          .delete(value.length() - index)
          .insert(endsWithNewlineChar(value.slice(0, index)) ? '' : '\n').ops,
      });
    }

    delta.insert('\n');

    const splittedValue = value.slice(index + prefixLengthToDelete);
    if (splittedValue.length() > 1) {
      delta.insert({ 'code-embed': splittedValue.ops }, blot.formats());
    }

    quill.updateContents(delta, Quill.sources.USER);
    quill.setSelection(blotOffset + advancedIndex, 0, Quill.sources.SILENT);
  }
}

export default CodeEmbedEditorKeyboard;
